import {useEffect, useRef, useState, type PointerEvent} from "react";
import type {AnswerPhoto} from "./activity-library";
import {drawPhotoStrokes, type PhotoAnnotation, type PhotoStroke} from "./photo-annotations";

function photoRotation(photo: AnswerPhoto) {
  return photo.rotation ?? 0;
}
function displaySize(size: {width:number;height:number}, rotation: number) {
  return rotation === 90 || rotation === 270
    ? {width:size.height,height:size.width}
    : size;
}
function rotatedImageStyle(size: {width:number;height:number}, rotation: number) {
  const swapped = rotation === 90 || rotation === 270;
  const display = displaySize(size, rotation);
  return {
    position: "absolute" as const,
    left: "50%",
    top: "50%",
    width: swapped ? `${display.height/display.width*100}%` : "100%",
    height: swapped ? `${display.width/display.height*100}%` : "100%",
    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
    transformOrigin: "center",
  };
}


export function AnnotatedPhoto({photo, annotation}: {photo: AnswerPhoto; annotation?: PhotoAnnotation}) {
  const layer = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({width: 1000, height: 1000});
  const rotation = photoRotation(photo), shown = displaySize(size, rotation);
  useEffect(() => {if (layer.current) drawPhotoStrokes(layer.current, annotation?.strokes ?? []);}, [annotation, shown.width, shown.height]);
  return <span className="annotated-photo" style={{width:`min(100%, ${280*shown.width/shown.height}px)`,aspectRatio:`${shown.width}/${shown.height}`}}>
    <img src={photo.url} alt={photo.name || "Photo de la réponse"} loading="lazy" style={rotatedImageStyle(size, rotation)}
      onLoad={e=>{const image=e.currentTarget; setSize({width: image.naturalWidth, height: image.naturalHeight});}} />
    <canvas ref={layer} width={shown.width} height={shown.height} aria-hidden="true" />
  </span>;
}

export function PhotoAnnotationEditor({photo, annotation, editable, onSave, onClose}: {
  photo: AnswerPhoto; annotation?: PhotoAnnotation; editable: boolean;
  onSave: (strokes: PhotoStroke[]) => Promise<void>; onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null), canvas = useRef<HTMLCanvasElement>(null);
  const initialStrokes = useRef<PhotoStroke[]>(structuredClone(annotation?.strokes ?? []));
  const strokes = useRef<PhotoStroke[]>(structuredClone(initialStrokes.current));
  const active = useRef<number | null>(null), dirty = useRef(false), closing = useRef(false);
  const [tool, setTool] = useState<"pen" | "eraser">("pen");
  const [color, setColor] = useState<PhotoStroke["color"]>("red");
  const [width, setWidth] = useState(.003);
  const [size, setSize] = useState({width: 1000, height: 1000});
  const rotation = photoRotation(photo), shown = displaySize(size, rotation);
  const [loaded, setLoaded] = useState(false), [saving, setSaving] = useState(false);
  const [error, setError] = useState(""), [count, setCount] = useState(strokes.current.length);
  const frame = useRef<number | null>(null);
  const repaint = () => {
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(()=>{frame.current=null; if(canvas.current) drawPhotoStrokes(canvas.current,strokes.current);});
  };
  useEffect(()=>{
    dialog.current?.showModal();
    const beforeUnload = (e: BeforeUnloadEvent) => {if(dirty.current){e.preventDefault(); e.returnValue="";}};
    window.addEventListener("beforeunload", beforeUnload);
    return ()=>{window.removeEventListener("beforeunload",beforeUnload); if(frame.current!==null) cancelAnimationFrame(frame.current);};
  },[]);
  useEffect(()=>{if(canvas.current) drawPhotoStrokes(canvas.current,strokes.current);},[shown.width,shown.height]);
  async function saveAndClose() {
    if (closing.current || active.current !== null) return;
    closing.current=true; setSaving(true); setError("");
    try {
      if (editable && dirty.current) await onSave(strokes.current);
      dirty.current=false; onClose();
    } catch(e) {setError(e instanceof Error ? e.message : "Enregistrement impossible. Ta correction reste ouverte.");}
    finally {closing.current=false; setSaving(false);}
  }
  function cancelAndClose() {
    if (closing.current || active.current !== null) return;
    if (editable && dirty.current && !window.confirm("Annuler les modifications non enregistrées de cette photo ?")) return;
    strokes.current = structuredClone(initialStrokes.current);
    dirty.current=false;
    setCount(strokes.current.length);
    onClose();
  }
  function clearAnnotations() {
    if (!editable || saving || active.current !== null || !strokes.current.length) return;
    strokes.current=[];
    dirty.current=true;
    setCount(0);
    repaint();
  }
  function point(e: PointerEvent<HTMLCanvasElement>) {
    const rect=e.currentTarget.getBoundingClientRect();
    const clamp=(n:number)=>Math.round(Math.min(1,Math.max(0,n))*100000)/100000;
    return {x:clamp((e.clientX-rect.left)/rect.width),y:clamp((e.clientY-rect.top)/rect.height)};
  }
  function start(e: PointerEvent<HTMLCanvasElement>) {
    if(!editable || saving || !loaded || active.current!==null || e.button!==0) return;
    const points=strokes.current.reduce((n,s)=>n+s.points.length,0);
    if(strokes.current.length>=500 || points>=50000){setError("Limite d’annotations atteinte. Annule un tracé avant de continuer.");return;}
    e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); active.current=e.pointerId;
    strokes.current.push({tool,color,width:tool==="eraser"?width*5:width,points:[point(e)]});
    dirty.current=true; setCount(strokes.current.length); repaint();
  }
  function move(e: PointerEvent<HTMLCanvasElement>) {
    if(active.current!==e.pointerId) return;
    e.preventDefault();
    if(strokes.current.reduce((n,s)=>n+s.points.length,0)>=50000) return;
    strokes.current[strokes.current.length-1].points.push(point(e)); repaint();
  }
  function end(e: PointerEvent<HTMLCanvasElement>) {
    if(active.current!==e.pointerId) return;
    active.current=null;
    if(e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  }
  return <dialog className="photo-annotation-dialog" ref={dialog} aria-label="Correction de la photo"
    onCancel={e=>{e.preventDefault();cancelAndClose();}} onClick={e=>{if(e.target===e.currentTarget) cancelAndClose();}}>
    <header><div><strong>{editable?"Corriger la photo":"Photo corrigée"}</strong><p>{editable?"Stylet, souris ou doigt · enregistrement uniquement avec le bouton prévu":"Consultation de la correction"}</p></div>
      <div className="photo-annotation-header-actions">
        {editable ? <>
          <button type="button" autoFocus disabled={saving} onClick={cancelAndClose}>Annuler les modifications</button>
          <button type="button" className="primary" disabled={saving} onClick={()=>void saveAndClose()}>{saving?"Enregistrement…":"Enregistrer et fermer"}</button>
        </> : <button type="button" autoFocus disabled={saving} onClick={onClose}>Fermer</button>}
      </div>
    </header>
    {editable && <div className="photo-annotation-toolbar" role="group" aria-label="Outils d’annotation">
      {([['black','Noir'],['red','Rouge'],['green','Vert'],['blue','Bleu']] as const).map(([value,label])=><button type="button" key={value} disabled={saving} aria-pressed={tool==="pen"&&color===value} onClick={()=>{setColor(value);setTool("pen");}}><span className={`pen-swatch pen-${value}`} />{label}</button>)}
      <button type="button" disabled={saving} aria-pressed={tool==="eraser"} onClick={()=>setTool("eraser")}>Gomme</button>
      <label>Épaisseur <select value={width} disabled={saving} onChange={e=>setWidth(Number(e.target.value))}><option value={.002}>Fine</option><option value={.003}>Moyenne</option><option value={.006}>Épaisse</option></select></label>
      <button type="button" disabled={saving||!count} onClick={()=>{strokes.current.pop();dirty.current=true;setCount(strokes.current.length);repaint();}}>Annuler le dernier tracé</button>
      <button type="button" className="photo-annotation-clear" disabled={saving||!count} onClick={clearAnnotations}>Tout effacer</button>
    </div>}
    {error && <p role="alert">{error}</p>}
    <div className="photo-annotation-workspace"><div className="photo-annotation-surface" style={{aspectRatio:`${shown.width}/${shown.height}`,width:`min(100%, calc((100dvh - 260px) * ${shown.width/shown.height}))`}}>
      <img src={photo.url} alt={photo.name || "Photo à corriger"} draggable={false} style={rotatedImageStyle(size, rotation)}
        onLoad={e=>{const img=e.currentTarget;const scale=Math.min(1,1800/Math.max(img.naturalWidth,img.naturalHeight));setSize({width:Math.max(1,Math.round(img.naturalWidth*scale)),height:Math.max(1,Math.round(img.naturalHeight*scale))});setLoaded(true);}}
        onError={()=>{setLoaded(false);setError("La photo n’a pas pu être chargée. Ferme puis réessaie.");}} />
      <canvas ref={canvas} width={shown.width} height={shown.height} role="img" aria-label="Zone d’annotation de la photo"
        className={editable?"editable":""} onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={end} onLostPointerCapture={end} />
    </div></div>
  </dialog>;
}
